import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { MobileShell } from "@/components/MobileShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CashIsleLogo } from "@/components/CashIsleLogo";
import { ArrowLeft, Youtube, Music2, Instagram, Loader2, HandCoins, Star, Briefcase, ShieldCheck, ShieldAlert } from "lucide-react";
import { HustleBadge, type HustleLevel } from "@/components/HustleBadge";
import { SKILL_LABEL, type WorkerSkill } from "@/lib/workerSkills";
import { toast } from "sonner";

interface CreatorProfile {
  user_id: string;
  display_name: string;
  cashtag: string;
  bio: string | null;
  youtube_url: string | null;
  spotify_url: string | null;
  tiktok_url: string | null;
  instagram_url: string | null;
  is_creator: boolean;
  kyc_status?: string | null;
}

const CreatorProfilePage = () => {
  const { cashtag = "" } = useParams<{ cashtag: string }>();
  const cleaned = cashtag.replace(/^\$/, "").toLowerCase();
  const navigate = useNavigate();
  const { session, user } = useAuth();
  const [profile, setProfile] = useState<CreatorProfile | null>(null);
  const [hustle, setHustle] = useState<{ score: number; level: HustleLevel } | null>(null);
  const [stats, setStats] = useState<{ completed_gigs: number; avg_stars: number; review_count: number; total_minutes: number } | null>(null);
  const [skills, setSkills] = useState<WorkerSkill[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reporting, setReporting] = useState(false);

  useEffect(() => {
    supabase
      .from("public_profiles")
      .select("user_id, display_name, cashtag, bio, youtube_url, spotify_url, tiktok_url, instagram_url, is_creator, kyc_status")
      .ilike("cashtag", cleaned)
      .maybeSingle()
      .then(async ({ data }) => {
        if (!data) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        setProfile(data as CreatorProfile);
        setLoading(false);
        const [{ data: s }, { data: sk }] = await Promise.all([
          supabase.rpc("get_worker_stats", { _user_id: (data as any).user_id }),
          supabase.from("worker_skills").select("skill").eq("user_id", (data as any).user_id),
        ]);
        const row = (s as any[] | null)?.[0];
        if (row) setStats({
          completed_gigs: Number(row.completed_gigs) || 0,
          avg_stars: Number(row.avg_stars) || 0,
          review_count: Number(row.review_count) || 0,
          total_minutes: Number(row.total_minutes) || 0,
        });
        if (sk) setSkills((sk as any[]).map((x) => x.skill));
      });

    supabase.rpc("get_hustle_for_cashtag", { _cashtag: cleaned }).then(({ data }) => {
      const row = (data as Array<{ score: number; level: string }> | null)?.[0];
      if (row) setHustle({ score: row.score, level: row.level as HustleLevel });
    });
  }, [cleaned]);

  const handleTip = () => {
    if (!profile) return;
    if (!session) {
      navigate(`/auth?next=/send?to=${profile.cashtag}`);
      return;
    }
    navigate(`/send?to=${profile.cashtag}`);
  };

  return (
    <MobileShell>
      <header className="bg-gradient-hero text-primary-foreground px-5 pt-8 pb-16 relative">
        <button
          onClick={() => navigate(-1)}
          aria-label="Back"
          className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-white/10 transition-smooth"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>

        <div className="flex flex-col items-center text-center mt-2">
          <div className="h-20 w-20 rounded-full bg-white/15 grid place-items-center backdrop-blur-sm">
            <CashIsleLogo size={48} />
          </div>
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin mt-4" />
          ) : profile ? (
            <>
              <h1 className="font-display text-2xl font-bold mt-4 flex items-center gap-1.5 justify-center">
                {profile.display_name}
                {profile.kyc_status === "verified" && (
                  <ShieldCheck className="h-5 w-5 text-accent" aria-label="Verified" />
                )}
              </h1>
              <p className="text-sm opacity-90 mt-1">${profile.cashtag}</p>
              {hustle && (
                <div className="mt-3">
                  <HustleBadge level={hustle.level} score={hustle.score} size="md" showScore />
                </div>
              )}
            </>
          ) : (
            <h1 className="font-display text-xl font-bold mt-4">Creator not found</h1>
          )}
        </div>
      </header>

      <div className="px-5 -mt-10 flex-1">
        {loading ? null : notFound ? (
          <div className="rounded-2xl bg-background shadow-card p-6 text-center">
            <p className="text-muted-foreground text-sm">
              No public Cash Isle creator at <span className="font-semibold">${cleaned}</span>.
            </p>
            <Link to="/" className="text-primary text-sm font-medium mt-3 inline-block">
              Back to Cash Isle
            </Link>
          </div>
        ) : profile ? (
          <div className="space-y-5">
            {profile.bio && (
              <div className="rounded-2xl bg-background shadow-card p-5">
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{profile.bio}</p>
              </div>
            )}

            {stats && stats.completed_gigs > 0 && (
              <div className="rounded-2xl bg-background shadow-card p-5 space-y-3">
                <p className="text-xs font-semibold uppercase text-muted-foreground flex items-center gap-1">
                  <Briefcase className="h-3 w-3" /> Hustle Hub stats
                </p>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="font-display font-extrabold text-xl">{stats.completed_gigs}</p>
                    <p className="text-[10px] uppercase text-muted-foreground">Gigs done</p>
                  </div>
                  <div>
                    <p className="font-display font-extrabold text-xl flex items-center justify-center gap-1">
                      {stats.avg_stars || "—"}
                      {!!stats.avg_stars && <Star className="h-4 w-4 fill-accent text-accent" />}
                    </p>
                    <p className="text-[10px] uppercase text-muted-foreground">{stats.review_count} reviews</p>
                  </div>
                  <div>
                    <p className="font-display font-extrabold text-xl">{Math.round(stats.total_minutes / 60)}h</p>
                    <p className="text-[10px] uppercase text-muted-foreground">Worked</p>
                  </div>
                </div>
              </div>
            )}

            {skills.length > 0 && (
              <div className="rounded-2xl bg-background shadow-card p-5 space-y-2">
                <p className="text-xs font-semibold uppercase text-muted-foreground">Skills</p>
                <div className="flex flex-wrap gap-1.5">
                  {skills.map((s) => (
                    <span key={s} className="text-xs bg-secondary rounded-full px-2.5 py-1 font-medium">
                      {SKILL_LABEL[s] ?? s}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <Button onClick={handleTip} variant="gold" size="xl" className="w-full">
              <HandCoins className="h-5 w-5 mr-2" />
              Tip ${profile.cashtag}
            </Button>

            <div className="grid grid-cols-2 gap-3">
              <SocialLink href={profile.youtube_url} label="YouTube" icon={<Youtube className="h-5 w-5" />} accent="hsl(0 84% 55%)" />
              <SocialLink href={profile.spotify_url} label="Spotify" icon={<Music2 className="h-5 w-5" />} accent="hsl(141 76% 36%)" />
              <SocialLink href={profile.tiktok_url} label="TikTok" icon={<TikTokIcon />} accent="hsl(var(--foreground))" />
              <SocialLink href={profile.instagram_url} label="Instagram" icon={<Instagram className="h-5 w-5" />} accent="hsl(330 81% 60%)" />
            </div>

            {!session && (
              <p className="text-xs text-center text-muted-foreground pt-2">
                <Link to="/auth" className="text-primary font-medium">Sign up</Link> to tip with one tap.
              </p>
            )}

            {user && profile.user_id !== user.id && (
              <div className="rounded-2xl bg-card border border-border p-4 space-y-2">
                <p className="text-sm font-semibold flex items-center gap-1">
                  <ShieldAlert className="h-4 w-4" /> Report this user
                </p>
                <Textarea
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  placeholder="Scam, harassment, fake profile, etc."
                  rows={2}
                  maxLength={500}
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  disabled={reporting || reportReason.trim().length < 5}
                  onClick={async () => {
                    setReporting(true);
                    const { error } = await supabase.from("gig_reports").insert({
                      reporter_id: user.id,
                      reported_user: profile.user_id,
                      reason: reportReason.trim().slice(0, 120),
                      details: reportReason.trim(),
                    });
                    setReporting(false);
                    if (error) toast.error(error.message);
                    else {
                      toast.success("Report submitted. Our team will review.");
                      setReportReason("");
                    }
                  }}
                >
                  Submit report
                </Button>
              </div>
            )}
          </div>
        ) : null}
      </div>

      <div className="safe-bottom" />
    </MobileShell>
  );
};

const SocialLink = ({
  href,
  label,
  icon,
  accent,
}: {
  href: string | null;
  label: string;
  icon: React.ReactNode;
  accent: string;
}) => {
  if (!href) {
    return (
      <div className="rounded-2xl bg-muted/50 p-4 flex items-center gap-3 opacity-50">
        <span className="text-muted-foreground">{icon}</span>
        <span className="text-sm text-muted-foreground">{label}</span>
      </div>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="rounded-2xl bg-background shadow-card p-4 flex items-center gap-3 hover:scale-[1.02] active:scale-[0.98] transition-smooth"
    >
      <span style={{ color: accent }}>{icon}</span>
      <span className="text-sm font-medium">{label}</span>
    </a>
  );
};

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden="true">
    <path d="M19.5 6.6a5.4 5.4 0 0 1-3.2-1V15a5.6 5.6 0 1 1-5.6-5.6c.3 0 .6 0 .9.1v2.7a2.9 2.9 0 1 0 2 2.8V2h2.6a5.4 5.4 0 0 0 3.3 4.6v0Z" />
  </svg>
);

export default CreatorProfilePage;
